import { useState, useEffect, useCallback } from 'react';
import { readabilityService } from '../../../../services/readability-service';

const AVAILABLE_GLYPHS = [
  'No Glyph',
  'Happy Face',
  'I Spy',
  'Hey April',
  'Dome',
  'Unicorn',
  'Noisy Tail',
  'Upper Cup',
  'Silly C',
  'Two Moons',
  'Hook',
  'That Noisy Th',
  'Aussie Oswald',
  'Ed Says',
  'J Dot',
  'Infinite',
  'Sharp Hat',
  'Treasure Chest',
  'Wonder One',
  'Say Cheese',
  'Fun Enough',
  'T Crossed D',
  'Silent'
];

const GLYPH_TO_PARAM = {
  'No Glyph': 'NO_GLYPH',
  'Happy Face': 'HAPPY_FACE',
  'I Spy': 'I_SPY',
  'Hey April': 'HEY_APRIL',
  'Dome': 'DOME',
  'Unicorn': 'UNICORN',
  'Noisy Tail': 'NOISY_TAIL',
  'Upper Cup': 'UPPER_CUP',
  'Silly C': 'SILLY_C',
  'Two Moons': 'TWO_MOONS',
  'Hook': 'HOOK',
  'That Noisy Th': 'THAT_NOISY_TH',
  'Aussie Oswald': 'AUSSIE_OSWALD',
  'Ed Says': 'ED_SAYS',
  'J Dot': 'J_DOT',
  'Infinite': 'INFINITE',
  'Sharp Hat': 'SHARP_HAT',
  'Treasure Chest': 'TREASURE_CHEST',
  'Wonder One': 'WONDER_ONE',
  'Say Cheese': 'SAY_CHEESE',
  'Fun Enough': 'FUN_ENOUGH',
  'T Crossed D': 'T_CROSSED_D',
  'Silent': 'SILENT'
};

const fetchGlyphData = async (glyph) => {
  try {
    const response = await readabilityService.lookupWordsByGlyph({ glyph });
    return response;
  } catch (error) {
    console.error('Error fetching glyph data:', error);
    throw error;
  }
};

export const useGlyphLookup = () => {
  const [selectedGlyph, setSelectedGlyph] = useState('');
  const [wordMap, setWordMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [totalWords, setTotalWords] = useState(0);

  const loadGlyphData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Convert glyph name to parameter format
      const glyphParam = GLYPH_TO_PARAM[selectedGlyph] || selectedGlyph.toLowerCase().replace(/\s+/g, '-');
      
      // Fetch data from API
      const data = await fetchGlyphData(glyphParam);
      
      setWordMap(data);
      setTotalWords(Object.keys(data).length);
      setPage(0); // Reset to first page when loading new data
    } catch (err) {
      console.error('Error loading glyph data:', err);
      setError('Failed to load glyph information. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedGlyph]);

  useEffect(() => {
    if (selectedGlyph) {
      loadGlyphData();
    }
  }, [selectedGlyph, loadGlyphData]);

  const handleGlyphChange = (event) => {
    setSelectedGlyph(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePlayAudio = () => {
    // In a real implementation, this would play the actual audio file
    setAudioPlaying(true);
    setTimeout(() => {
      setAudioPlaying(false);
    }, 2000); // Simulate audio playing for 2 seconds
  };

  const getDisplayedWords = () => {
    const wordEntries = Object.entries(wordMap);
    
    // Apply pagination
    const paginatedEntries = wordEntries.slice(
      page * rowsPerPage, 
      page * rowsPerPage + rowsPerPage
    );
    
    // Transform entries into a more usable format for the table
    return paginatedEntries.map(([word, details]) => ({
      word: word,
      readable: details.homographs && details.homographs.length > 0 ? details.homographs[0] : "",
      homophones: details.homophones || []
    }));
  };

  return {
    // Constants
    availableGlyphs: AVAILABLE_GLYPHS,
    
    // State
    selectedGlyph,
    wordMap,
    loading,
    error,
    page,
    rowsPerPage,
    audioPlaying,
    totalWords,
    
    // Actions
    handleGlyphChange,
    handleChangePage,
    handleChangeRowsPerPage,
    handlePlayAudio,
    loadGlyphData,
    
    // Computed values
    hasSelection: !!selectedGlyph,
    hasWords: Object.keys(wordMap).length > 0,
    displayedWords: wordMap ? getDisplayedWords() : []
  };
};